// キャンバス
import React, { useRef, useCallback, useEffect } from 'react';
import { KonvaEventObject } from 'konva/types/Node';
import { Stage, Layer, Line, Image as CanvasImage } from 'react-konva';
import { Stage as StageType } from 'konva/types/Stage';
import { compressor_option, preview_option } from '../Const';
import PreviewModal from './PreviewModal';

interface Props {
  img_src: string;
  disabled: boolean;
  handleFinish: (image_file: string) => void;
  block_disabled?: boolean;
  file_download: string;
  file_name: string;
  is_pdf: boolean;
}

const Canvas: React.FC<Props> = ({
  img_src,
  disabled,
  handleFinish,
  block_disabled,
  file_download,
  file_name,
  is_pdf,
}) => {
  const [lines, setLines] = React.useState<Array<number[]>>([]);
  const [can_drawing, setCanDrawing] = React.useState(true); // 常時trueとする
  const [is_drawing, setIsDrawing] = React.useState(false);
  const [canvas_image] = React.useState(new Image());
  const [canvas_width, setCanvasWidth] = React.useState(0);
  const [canvas_heigth, setCanvasHeight] = React.useState(0);
  const [is_disabled_button_masking, setIsDisabledButtonMasking] = React.useState(disabled);
  const [is_disabled_button_reset, setIsDisabledButtonReset] = React.useState(true);
  const [is_disabled_button_finish, setIsDisabledButtonFinish] = React.useState(true);
  const [current_file_name, setCurrentFileName] = React.useState('');
  const stageRef = useRef<StageType>(null);
  const [can_pinchIn, setCanPinchIn] = React.useState(false);
  const [is_preview, setIsPreview] = React.useState(false);

  useEffect(() => {
    //コンポーネント表示中はタッチ操作でピンチインピンチアウトを無効化する。
    window.addEventListener('touchstart', handlePinchInStart);
    window.addEventListener('touchend', handlePinchInEnd);
    return () => {
      window.removeEventListener('touchstart', handlePinchInStart);
      window.removeEventListener('touchend', handlePinchInEnd);
    };
  }, []);

  React.useEffect(() => {
    if (is_pdf) {
      setIsDisabledButtonMasking(true);
    } else {
      setIsDisabledButtonMasking(disabled);
    }
    setIsDisabledButtonReset(true);
    setIsDisabledButtonFinish(true);
    setLines([]);

    canvas_image.onload = () => {
      //ファイルのサイズに応じてCanvasのサイズを設定する。

      //参照ボタン押下で別ファイルが読み込まれた場合
      if (file_name !== null && current_file_name !== file_name) {
        //読込んだ画像の横幅と縦幅を取得
        let result = { width: canvas_image.naturalWidth, height: canvas_image.naturalHeight };

        //横幅が縦幅より長い場合は横幅を圧縮時の最大サイズに設定し、縦幅をアスペクト比に応じて設定する。
        if (result.width >= result.height) {
          const base_size =
            result.width <= compressor_option.preview_max_width
              ? result.width
              : compressor_option.preview_max_width;
          const magnification = result.height / result.width;

          result.width = base_size;
          result.height = base_size * magnification;
        } else {
          const base_size =
            result.height <= compressor_option.preview_max_height
              ? result.width
              : compressor_option.preview_max_height;
          const magnification = result.width / result.height;
          result.width = base_size * magnification;
          result.height = base_size;
        }

        setCanvasWidth(result.width);
        setCanvasHeight(result.height);
        setCurrentFileName(file_name);
      }
    };
    canvas_image.src = img_src;
  }, [disabled, img_src]);

  /**
   * canvas描画イメージを取得する。
   * @return string canvas描画イメージのdataUrl
   */
  const getFile = (): string => {
    //画像保存時のプレビュー画像の拡大倍率(拡大倍率は圧縮時の最大画像サイズ以内とする)
    const scale =
      canvas_image.naturalWidth > compressor_option.max_width
        ? compressor_option.max_width / canvas_width
        : canvas_image.naturalWidth / canvas_width;

    const image_file = stageRef.current?.toDataURL({ pixelRatio: scale }) ?? '';
    return image_file;
  };

  /**
   * タッチ操作開始時のイベント
   */
  const handlePinchInStart = (event: TouchEvent): void => {
    const touches = event.touches;
    //ピンチイン操作の判定を行う。
    setCanPinchIn(touches.length > 1);
  };

  /**
   * タッチ操作終了時のイベント
   */
  const handlePinchInEnd = (event: TouchEvent): void => {
    //ピンチイン操作フラグを無効にする。
    setCanPinchIn(false);
  };

  /**
   * マスキング描画開始時のイベント
   * @param event タッチイベント | マウスイベント
   */
  const handleStart = (
    event: KonvaEventObject<globalThis.TouchEvent> | KonvaEventObject<globalThis.MouseEvent>
  ) => {
    if (!can_drawing) {
      return;
    }
    setIsDrawing(true);
    const stage = event.target.getStage();
    if (stage !== null) {
      const point = stage.getPointerPosition();
      if (point !== null) {
        setLines([...lines, [point.x, point.y]]);
      }
    }
  };

  /**
   * マスキング描画移動時のイベント
   * @param event タッチイベント | マウスイベント
   */
  const handleMove = (
    event: KonvaEventObject<globalThis.TouchEvent> | KonvaEventObject<globalThis.MouseEvent>
  ) => {
    // no drawing - skipping
    if (!is_drawing) {
      return;
    }
    const stage = event.target.getStage();
    if (stage !== null) {
      const point = stage.getPointerPosition();
      if (point !== null) {
        const lastLine = lines[lines.length - 1].concat([point.x, point.y]);
        lines.splice(lines.length - 1, 1, lastLine);
        setLines(lines.concat());
      }
    }
  };

  /**
   * マスキング描画終了時のイベント
   */
  const handleEnd = () => {
    setIsDrawing(false);
    setCanPinchIn(false);
  };

  /**
   * マスキングボタン押下時のイベント
   */
  const handleMasking = () => {
    setIsDisabledButtonMasking(true);
    setIsDisabledButtonReset(false);
    setIsDisabledButtonFinish(false);
    setCanPinchIn(false);

    // 確定処理を実行する
    handleFinish(getFile());
  };

  /**
   * 確定ボタン押下時のイベント（活性・非活性・ボタン制御）
   */
  const handleFinishDisabled = useCallback(() => {
    setIsDisabledButtonMasking(false);
    setIsDisabledButtonReset(true);
    setIsDisabledButtonFinish(true);
  }, []);

  /**
   * リセットボタン押下時のイベント
   */
  const handleReset = () => {
    setLines([]);
  };

  /**
   * プレビュー画像押下時のイベント
   * @param file_name プレビュー表示およびダウンロードするファイル
   */
  const handlePreview = (file_name: string) => {
    // マスキング中またはダウンロード対象がpdfかファイル名がない場合は処理を終了する。
    if (can_drawing || is_pdf || !file_name) {
      return;
    }
    setIsPreview(true);
  };

  /**
   * プレビュー表示時の画像サイズを計算する。
   * @param image_width 棚に表示中の画像の横幅
   * @param image_height 棚に表示中の画像の縦幅
   * @return object プレビュー画像の縦横幅の設定値オブジェクト
   */
  const imageSize = (image_width: number, image_height: number) => {
    const result = { preview_width: 0, preview_height: 0 };
    // 比率決定
    const reduction_rate =
      window.innerWidth > 600 ? preview_option.reduction_rate_pc : preview_option.reduction_rate_sp;

    //プレビュー画像のサイズを設定する。
    if (image_width > image_height) {
      const tmp_size = window.innerHeight * reduction_rate;
      const base_size =
        window.innerWidth < tmp_size ? window.innerWidth * reduction_rate : tmp_size;
      result.preview_width = base_size;
      result.preview_height = base_size * (image_height / image_width);
    } else if (image_width < image_height) {
      const tmp_size = window.innerWidth * reduction_rate;
      const base_size =
        window.innerHeight < tmp_size ? window.innerHeight * reduction_rate : tmp_size;
      result.preview_width = base_size * (image_width / image_height);
      result.preview_height = base_size;
    } else {
      const tmp_size = window.innerWidth * reduction_rate;
      result.preview_width = tmp_size;
      result.preview_height = tmp_size;
    }

    return result;
  };

  /**
   * プレビューモーダルを非表示にする。
   */
  const handleClose = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setIsPreview(false);
  }, []);

  return (
    <div className="text-center">
      <div className="d-inline-block mr-1 mt-1" onClick={() => handlePreview(file_download)}>
        {/* マスキング処理中 */}
        {can_drawing && !can_pinchIn && (
          <div className="preview-image">
            <Stage
              ref={stageRef}
              width={canvas_width}
              height={canvas_heigth}
              onMouseDown={handleStart}
              onMouseUp={handleEnd}
              onMouseMove={handleMove}
              onTouchStart={handleStart}
              onTouchEnd={handleEnd}
              onTouchMove={handleMove}
            >
              <Layer>
                <CanvasImage
                  image={canvas_image}
                  width={canvas_width}
                  height={canvas_heigth}
                  x={0}
                  y={0}
                />
                {lines.map((line, i) => (
                  <Line
                    key={i}
                    points={line}
                    stroke="black"
                    strokeWidth={8}
                    tension={0.5}
                    lineCap="round"
                    globalCompositeOperation={'source-over'}
                  />
                ))}
              </Layer>
            </Stage>
          </div>
        )}
        {/* マスキング処理中でない場合 */}
        {(!can_drawing || can_pinchIn) && (
          <img
            style={{ width: canvas_width, height: canvas_heigth }}
            src={canvas_image.src}
            width={canvas_image.naturalWidth}
            height={canvas_image.naturalHeight}
          />
        )}
      </div>
      <div>
        <div className="mask-edit-control">
          <div className="control-right">
            <div className="c-btn btn-large btn-green" onClick={handleMasking}>
              登録する
            </div>
          </div>
          <div className="control-left">
            <div className="c-btn btn-large btn-white" onClick={handleReset}>
              リセットする
            </div>
          </div>
        </div>
      </div>
      <PreviewModal
        show={is_preview}
        image_source={canvas_image.src}
        image_width={imageSize(canvas_width, canvas_heigth).preview_width}
        image_height={imageSize(canvas_width, canvas_heigth).preview_height}
        handleOnclick={handleClose}
      />
    </div>
  );
};

export default Canvas;
